import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { FileText, ShoppingCart, PackagePlus, AlertCircle } from "lucide-react";
import { listMyRfqs, listMyPurchaseOrders, listMyGrns, getVendorMe } from "@/lib/vendor-portal.functions";

export const Route = createFileRoute("/vendor/")({
  component: VendorDashboard,
});

function VendorDashboard() {
  const me = useServerFn(getVendorMe);
  const rfqsFn = useServerFn(listMyRfqs);
  const posFn = useServerFn(listMyPurchaseOrders);
  const grnsFn = useServerFn(listMyGrns);

  const meQ = useQuery({ queryKey: ["vendor", "me"], queryFn: () => me({}) });
  const rfqQ = useQuery({ queryKey: ["vendor", "rfqs"], queryFn: () => rfqsFn({}) });
  const poQ = useQuery({ queryKey: ["vendor", "pos"], queryFn: () => posFn({}) });
  const grnQ = useQuery({ queryKey: ["vendor", "grns"], queryFn: () => grnsFn({}) });

  const rfqs = rfqQ.data?.rfqs ?? [];
  const quotations = rfqQ.data?.quotations ?? [];
  const submittedRfqIds = new Set(quotations.map((q: any) => q.rfq_id));
  const openRfqs = rfqs.filter((r: any) => !submittedRfqIds.has(r.id));

  const pos = poQ.data?.pos ?? [];
  const pendingAck = pos.filter((p: any) => !p.acknowledged_at);

  const grns = grnQ.data?.grns ?? [];
  const recentGrns = grns.slice(0, 5);

  const vendor = meQ.data?.vendor as any;
  const missing: string[] = [];
  if (vendor) {
    if (!vendor.gstin) missing.push("GSTIN");
    if (!vendor.pan) missing.push("PAN");
    if (!vendor.phone) missing.push("Phone");
    if (!vendor.address) missing.push("Address");
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Welcome, {vendor?.name ?? "Vendor"}</h1>
        <p className="text-sm text-muted-foreground">Vendor code: {vendor?.code ?? "—"}</p>
      </div>

      {missing.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
          <AlertCircle className="mt-0.5 h-4 w-4 text-amber-600" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">Complete your profile</p>
            <p className="text-amber-800">Missing: {missing.join(", ")}.</p>
          </div>
          <Link to="/vendor/profile" className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white">Update</Link>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile icon={FileText} label="Open RFQs" value={openRfqs.length} to="/vendor/rfqs" />
        <Tile icon={FileText} label="Submitted Quotations" value={quotations.length} to="/vendor/rfqs" />
        <Tile icon={ShoppingCart} label="POs Pending Ack." value={pendingAck.length} to="/vendor/purchase-orders" />
        <Tile icon={PackagePlus} label="Total GRNs" value={grns.length} to="/vendor/grns" />
      </div>

      <section>
        <h2 className="mb-3 text-lg font-semibold">Open RFQs</h2>
        <div className="rounded-md border bg-card">
          {openRfqs.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No open RFQs awaiting quotation.</p>
          ) : (
            <ul className="divide-y">
              {openRfqs.slice(0, 5).map((r: any) => (
                <li key={r.id} className="flex items-center justify-between p-3 text-sm">
                  <div>
                    <p className="font-medium">{r.rfq_no ?? r.title ?? r.id.slice(0, 8)}</p>
                    <p className="text-xs text-muted-foreground">Due: {r.response_deadline ?? "—"}</p>
                  </div>
                  <Link to="/vendor/rfqs/$rfqId" params={{ rfqId: r.id }} className="text-primary hover:underline">
                    View & Quote →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold">POs pending acknowledgement</h2>
        <div className="rounded-md border bg-card">
          {pendingAck.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">All POs acknowledged.</p>
          ) : (
            <ul className="divide-y">
              {pendingAck.slice(0, 5).map((p: any) => (
                <li key={p.id} className="flex items-center justify-between p-3 text-sm">
                  <div>
                    <p className="font-medium">{p.po_number}</p>
                    <p className="text-xs text-muted-foreground">Total: ₹{Number(p.total_amount ?? 0).toLocaleString()} · Date: {p.po_date}</p>
                  </div>
                  <Link to="/vendor/purchase-orders/$poId" params={{ poId: p.id }} className="text-primary hover:underline">
                    View →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold">Recent Deliveries</h2>
        <div className="rounded-md border bg-card">
          {recentGrns.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No GRNs yet.</p>
          ) : (
            <ul className="divide-y">
              {recentGrns.map((g: any) => (
                <li key={g.id} className="flex items-center justify-between p-3 text-sm">
                  <div>
                    <p className="font-medium">{g.grn_no}</p>
                    <p className="text-xs text-muted-foreground">PO: {g.po_number ?? "—"} · Date: {g.received_date}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function Tile({ icon: Icon, label, value, to }: { icon: any; label: string; value: number; to: string }) {
  return (
    <Link to={to} className="block rounded-lg border bg-card p-4 hover:border-primary">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="mt-2 text-3xl font-semibold">{value}</p>
    </Link>
  );
}
